/**
 * Certificate View - Form 11 style certificate
 * API: /api/v1/dummy/inspection-response
 */

import { Box, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import './CertificateView.css';

const CERT_HEADER = {
  formNumber: 'FORM 11',
  ruleRef: '(See Rule 62 and 64)',
  title: 'Report of Examination of Hoists, Lift, Lifting Machine, Chains, Ropes and Lifting Tackles',
};

const CERT_FOOTER = {
  officeAddressLine1: 'Chitanvispura, Shivaji Nagar,',
  officeAddressLine2: 'Mahal, Nagpur 440032.',
  officeAddressLine3: 'Contact No: +91 7721010291.',
  authority: 'Amol Zalke',
  designation: 'BE (MECH), PGDTD CAD-CAE',
  competentAuth: 'Competent Authority for Safety and Health',
  msDish: 'MS/DISH/CPC/Z-002/2025',
};

const CELL_SX = { border: '0.5px solid #000', py: 0.35, px: 0.75, textAlign: 'left', verticalAlign: 'top', fontSize: '0.8125rem' };
const COUNT_CELL_SX = { ...CELL_SX, width: 40, minWidth: 40, textAlign: 'center' };
const LABEL_CELL_SX = { ...CELL_SX, width: '42%' };
const VALUE_CELL_SX = { ...CELL_SX, fontWeight: 400 };

function IdentificationDescCell({ data }) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return (
    <Box sx={{ '& > div': { mb: 1 } }}>
      {Object.values(data).map((val, i) => (
        <Box key={i} component="div">{String(val ?? '')}</Box>
      ))}
    </Box>
  );
}

function parseInspectionPoints(data) {
  const sectionA = []; let sectionB = null; const sectionC = [];
  if (!data || typeof data !== 'object') return { sectionA, sectionB, sectionC };
  Object.entries(data).forEach(([label, value]) => {
    const lb = label.toLowerCase();
    if (lb.includes('maintenance') && lb.includes('inaccessible') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.entries(value).forEach(([k, v]) => sectionA.push({ label: k, value: String(v ?? '') }));
    } else if (lb.includes('point c') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.entries(value).forEach(([k, v]) => sectionC.push({ label: k, value: String(v ?? '') }));
    } else { sectionB = { label, value: String(value ?? '') }; }
  });
  return { sectionA, sectionB, sectionC };
}

function findKey(data, contains) {
  const k = Object.keys(data || {}).find((key) => key.includes(contains));
  return k ? data[k] : '';
}

export default function CertificateView({ data }) {
  if (!data) return null;
  const { occupierName, factoryAddress, identificationDesc = {}, inspectionPoints = {} } = data;
  const dateOfExamination = findKey(data, 'Date of each examination') || findKey(data, 'Sec. 29(1)') || '';
  const repairsRenewals = findKey(data, 'Repairs, renewals or alterations') || '';

  const certNumber = data.certificateNumber || data.certificateNo || '7375';
  const equipmentType = data.equipmentType || (identificationDesc && Object.values(identificationDesc).some(v => /pick.*carry|carry.*pick/i.test(String(v))) ? 'Pick & Carry' : '');
  const certLine = `Maharashtra Factories Rules 1963. Certificate No.${certNumber}-S/M/S ${occupierName || '—'}${equipmentType ? ` /${equipmentType}` : ''}`;

  return (
    <Box className="certificate-view" sx={{ width: '210mm', maxWidth: '100%', minHeight: '297mm', mx: 'auto', p: 0.5, bgcolor: '#fff', color: '#222', boxSizing: 'border-box' }}>
      <header className="cert-header">
        <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>{CERT_HEADER.formNumber}</Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', color: '#222', mb: 0.5 }}>{CERT_HEADER.ruleRef}</Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', color: '#222', mb: 0.5 }}>{CERT_HEADER.title}</Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', color: '#222' }}>{certLine}</Typography>
      </header>

      <section className="cert-body">
        <TableContainer sx={{ width: '100%' }}>
          <Table size="small" className="cert-table">
            <TableBody>
              <TableRow><TableCell sx={COUNT_CELL_SX}>1</TableCell><TableCell sx={LABEL_CELL_SX}>Name of the Occupier:</TableCell><TableCell sx={{ ...VALUE_CELL_SX, fontWeight: 700 }}>{occupierName || '—'}</TableCell></TableRow>
              <TableRow><TableCell sx={COUNT_CELL_SX}>2</TableCell><TableCell sx={LABEL_CELL_SX}>Name and Address of Factory Site At:</TableCell><TableCell sx={VALUE_CELL_SX}>{factoryAddress || '—'}</TableCell></TableRow>
              <TableRow>
                <TableCell sx={COUNT_CELL_SX}>3</TableCell>
                <TableCell sx={LABEL_CELL_SX}>(a) Type, Identification number and description of hoist, lift, lifting machine, chains, ropes, and lifting tackles.<br /><br />(b) Date of construction or reconstruction and the date when the hoist, lift, lifting machine, ropes and lifting tackles were first taken into use in the factory.</TableCell>
                <TableCell sx={VALUE_CELL_SX}>{Object.keys(identificationDesc || {}).length > 0 ? <IdentificationDescCell data={identificationDesc} /> : '—'}</TableCell>
              </TableRow>
              <TableRow><TableCell sx={COUNT_CELL_SX}>4</TableCell><TableCell sx={LABEL_CELL_SX}>Date of each examination made under Section 28(1)(a)(ii) and Sec. 29(1)(a)(iii) and by whom it was carried out.</TableCell><TableCell sx={VALUE_CELL_SX}>{dateOfExamination || '—'}</TableCell></TableRow>
              {(() => {
                const { sectionA, sectionB, sectionC } = parseInspectionPoints(inspectionPoints);
                const hasAny = sectionA.length > 0 || sectionB || sectionC.length > 0;
                const subCellSx = { ...CELL_SX, py: 0.25, px: 0.75, fontSize: '0.75rem', border: 'none', borderTop: 'none' };
                const subLabelSx = { ...subCellSx, width: '55%' };
                const subValueSx = { ...subCellSx, fontWeight: 400 };
                const totalRows = hasAny
                  ? 1 + (sectionA.length > 0 ? sectionA.length : 0) + (sectionB && sectionA.length > 0 ? 1 : 0) + (sectionC.length > 0 ? sectionC.length : 0)
                  : 1;
                return (
                  <>
                    <TableRow>
                      <TableCell rowSpan={hasAny ? totalRows : 1} sx={{ ...COUNT_CELL_SX, verticalAlign: 'top' }}>5</TableCell>
                      <TableCell sx={LABEL_CELL_SX}>
                        (A) Maintenance: (List of parts, if any, which were Inaccessible) Are the following parts of the hoist of lift properly maintained and in good working order? If not, state what defects have been found:-
                        {sectionA.length === 0 && sectionB && <><br /><br />(B) {sectionB.label}</>}
                      </TableCell>
                      <TableCell sx={VALUE_CELL_SX}>
                        {sectionA.length > 0 ? '' : (sectionB ? sectionB.value : (sectionC.length > 0 ? '' : '—'))}
                      </TableCell>
                    </TableRow>
                    {sectionA.length > 0 && sectionA.map(({ label, value }, i) => (
                      <TableRow key={`a-${i}`}>
                        <TableCell sx={subLabelSx}>{label}</TableCell>
                        <TableCell sx={subValueSx}>{value}</TableCell>
                      </TableRow>
                    ))}
                    {sectionB && sectionA.length > 0 && (
                      <TableRow>
                        <TableCell sx={subLabelSx}>(B) {sectionB.label}</TableCell>
                        <TableCell sx={subValueSx}>{sectionB.value}</TableCell>
                      </TableRow>
                    )}
                    {sectionC.length > 0 && sectionC.map(({ label, value }, i) => (
                      <TableRow key={`c-${i}`}>
                        <TableCell sx={subLabelSx}>(C) {label}</TableCell>
                        <TableCell sx={subValueSx}>{value}</TableCell>
                      </TableRow>
                    ))}
                  </>
                );
              })()}
              <TableRow><TableCell sx={COUNT_CELL_SX}>6</TableCell><TableCell sx={LABEL_CELL_SX}>Repairs, renewals or alterations (if any) required and the period within which they should be executed and Maximum safe working load subject to repairs, renewals or alternations (if any)</TableCell><TableCell sx={VALUE_CELL_SX}>{repairsRenewals || '—'}</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </section>

      {/* Certification paragraph + Footer */}
      <footer>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          I/We certify that on <strong>05.03.2026</strong>, I/We thoroughly examined the hoist, lift, lifting machine, ropes and lifting tackles and details of examinations/tests carried out is enclosed herewith that the above is a correct report of the result. Certificate Valid up to <strong>04.09.2026</strong> or change in working height/location whichever occurs earlier.
        </Typography>
        <Box sx={{ borderTop: '1px solid', borderColor: 'grey.300', pt: 0.5, mt: 0.5, display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 0 }}>
            <Box component="img" src={`${process.env.PUBLIC_URL || ''}/absolutesafetylogo.png`} 
            alt="Absolute Safety" sx={{ height: 88, width: 'auto', objectFit: 'contain', mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>Office Address</Typography>
            <Typography variant="body2">{CERT_FOOTER.officeAddressLine1}</Typography>
            <Typography variant="body2">{CERT_FOOTER.officeAddressLine2}</Typography>
            <Typography variant="body2">{CERT_FOOTER.officeAddressLine3}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', p: 0, mt: 1 }}>
            <Box sx={{ width: 180, height: 48, borderBottom: '1px solid #333', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{CERT_FOOTER.authority}</Typography>
            <Typography variant="body2">{CERT_FOOTER.designation}</Typography>
            <Typography variant="body2">{CERT_FOOTER.competentAuth}</Typography>
            <Typography variant="body2">{CERT_FOOTER.msDish}</Typography>
          </Box>
        </Box>
      </footer>
    </Box>
  );
}
